import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAnalytics } from "hooks/useAnalytics";
import { getScreenNameFromPath } from "analytics";
import { isRedirectOnlyPath } from "lib/analytics/page-view-tracking";
import {
  buildPosthogPageLeaveProperties,
  RIVETAI_ANALYTICS_SURFACE,
} from "lib/analytics/posthog-pageview";
import { capturePosthogPageLeave } from "config/posthog-runtime";

/**
 * Tracks page/screen views on route change. Mount once inside the Router.
 */
export const usePageViewTracking = () => {
  const location = useLocation();
  const { trackPageView } = useAnalytics();
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    const pathname = location.pathname;
    if (pathname.startsWith("/s/")) return;
    if (isRedirectOnlyPath(pathname)) return;
    if (previousPathRef.current === pathname) return;

    const previousPath = previousPathRef.current;
    previousPathRef.current = pathname;

    if (previousPath) {
      const { name: previousScreenName } = getScreenNameFromPath(previousPath);
      capturePosthogPageLeave({
        ...buildPosthogPageLeaveProperties(RIVETAI_ANALYTICS_SURFACE.productApp, previousPath),
        screen_name: previousScreenName,
      });
    }

    const { name, properties } = getScreenNameFromPath(pathname);
    trackPageView(name, {
      screen_path: pathname,
      ...properties,
    });
  }, [location.pathname, trackPageView]);
};
